/**
 * Opponent strength for "Practice with Computer" chapters.
 *
 * Before this existed the practice opponent searched at the same depth as the
 * analysis engine (24) and always played the top PV — i.e. full strength,
 * which made the `equalize` and `advantage` goals unreachable for the students
 * the mode is actually for.
 *
 * Stored on the chapter as `practice_difficulty`: a nullable target Elo.
 * `null` means "no limit" and is the pre-existing behaviour, so chapters saved
 * before this field existed keep playing exactly as they did.
 *
 * 1320 is Stockfish's own floor for UCI_Elo; anything lower is clamped by the
 * engine itself, so LEVELS starts there rather than pretending to go below it.
 */

export type PracticeDifficulty = {
  key: string;
  label: string;
  desc: string;
  /** UCI_Elo target. null = UCI_LimitStrength off (full strength). */
  elo: number | null;
  /** Search budget for the opponent's reply — shallow keeps it responsive. */
  depth: number;
  movetime: number;
};

export const PRACTICE_DIFFICULTIES: PracticeDifficulty[] = [
  {
    key: 'beginner',
    label: 'Beginner',
    desc: 'Plays simply and misses tactics.',
    elo: 1320,
    depth: 5,
    movetime: 400,
  },
  {
    key: 'easy',
    label: 'Easy',
    desc: 'Punishes obvious blunders.',
    elo: 1600,
    depth: 7,
    movetime: 600,
  },
  {
    key: 'intermediate',
    label: 'Intermediate',
    desc: 'Solid club-player strength.',
    elo: 2000,
    depth: 10,
    movetime: 900,
  },
  {
    key: 'hard',
    label: 'Hard',
    desc: 'Strong — expect to be punished.',
    elo: 2400,
    depth: 14,
    movetime: 1200,
  },
  {
    key: 'max',
    label: 'Maximum',
    desc: 'Full engine strength. Effectively unbeatable.',
    elo: null,
    depth: 18,
    movetime: 1500,
  },
];

export const DEFAULT_PRACTICE_DIFFICULTY_KEY = 'easy';

export function difficultyByKey(key: string | null | undefined) {
  return (
    PRACTICE_DIFFICULTIES.find(d => d.key === key) ||
    PRACTICE_DIFFICULTIES.find(d => d.key === DEFAULT_PRACTICE_DIFFICULTY_KEY)!
  );
}

/**
 * Chapters store the Elo, not the key, so an author can in principle set any
 * target and the level list can be re-tuned without a data migration. Maps a
 * stored Elo back onto the nearest level for display.
 */
export function difficultyFromElo(
  elo: number | null | undefined,
): PracticeDifficulty {
  if (elo === null || elo === undefined) {
    return PRACTICE_DIFFICULTIES[PRACTICE_DIFFICULTIES.length - 1];
  }
  let best = PRACTICE_DIFFICULTIES[0];
  let bestGap = Infinity;
  for (const level of PRACTICE_DIFFICULTIES) {
    if (level.elo === null) {
      continue;
    }
    const gap = Math.abs(level.elo - elo);
    if (gap < bestGap) {
      bestGap = gap;
      best = level;
    }
  }
  return best;
}

export type PracticeStrengthPolicy = 'fixed' | 'floor' | 'free' | null;

/** null Elo means "Maximum", i.e. the strongest setting, not the weakest. */
function comparableElo(elo: number | null): number {
  return elo == null ? Number.POSITIVE_INFINITY : elo;
}

/**
 * Applies the coach's strength policy to whatever level is actually selected.
 *
 * The picker already hides the levels a student may not choose, but that's
 * presentation: a choice stored before the coach added the policy (or any
 * other route into this state) would otherwise keep feeding a weaker Elo
 * straight to the engine. Clamping here means the policy holds at the one
 * place that decides how hard the opponent really plays.
 *
 * `null`/'free' policy returns the choice untouched — the pre-existing
 * behaviour for every chapter authored before the setting existed.
 */
export function applyStrengthPolicy(
  chosen: PracticeDifficulty,
  policy: PracticeStrengthPolicy,
  coachElo: number | null | undefined,
): PracticeDifficulty {
  if (!policy || policy === 'free') {
    return chosen;
  }
  const coachLevel = difficultyFromElo(coachElo ?? null);
  if (policy === 'fixed') {
    return coachLevel;
  }
  return comparableElo(chosen.elo) < comparableElo(coachLevel.elo)
    ? coachLevel
    : chosen;
}
